package com.arena_web.ui.components.overlay

import androidx.compose.runtime.*
import com.arena_web.data.dto.PersonDto
import com.arena_web.data.dto.ProfileCardDto
import com.arena_web.data.dto.UserAwardDto
import com.arena_web.data.dto.UserPostDto
import com.arena_web.di.LocalAppContainer
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.cards.SwipeableDeckCard
import com.arena_web.ui.components.feed.PostImageCollage
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.icons.TintedGlyph
import com.arena_web.ui.components.widgets.HiddenScrollbarStyle
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.sections.profile.AwardsGridContent
import com.arena_web.ui.sections.profile.edit.GradientBorderRing
import com.arena_web.ui.sections.profile.edit.InterestCatalog
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.document
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.w3c.dom.events.Event

private const val DIALOG_BG = "var(--l-s2, #09142d)"

// Figma 7636:196536 — outline chip / pill background tint used across the popup
// (Following / Followers, zodiac, inactive interest chips).
private val INTERACTIVE_TRANSLUCENT_BG =
    AppGradients.InteractiveSecondary

private enum class DetailTab {
    About, Posts, Achievement
}

/**
 * Person-detail popup opened via the Info button on the People page card.
 * Layout matches Figma 7636:196536 (About tab) and 7636:196682 (Posts tab):
 * card preview on the left, profile info + tab content on the right.
 *
 * Posts and Achievement tabs render placeholders for now — the feed-level
 * spec only required Like/Skip, so a full posts integration is deferred.
 */
@Composable
fun PersonDetailDialog(
    person: PersonDto?,
    sportName: String,
    onDismiss: () -> Unit,
) {
    if (person == null) return
    var activeTab by remember(person.userId) { mutableStateOf(DetailTab.About) }

    // Load this person's profile-card gallery. When >1 card is returned the
    // left column renders a carousel ("1 / N" + prev/next arrows, Figma
    // 7636:196537); otherwise we fall back to the single card the feed
    // already provided.
    val appContainer = LocalAppContainer.current
    val cardRepository = appContainer.cardRepository
    val userRepository = appContainer.userRepository
    val feedRepository = appContainer.feedRepository
    val scope = rememberCoroutineScope()
    var gallery by remember(person.userId) { mutableStateOf<List<ProfileCardDto>>(emptyList()) }
    var galleryIndex by remember(person.userId) { mutableStateOf(0) }
    LaunchedEffect(person.userId) {
        galleryIndex = 0
        gallery = emptyList()
        cardRepository?.getUserProfileCards(person.userId)?.onSuccess { cards ->
            // Sort by displayOrder so navigation matches the owner's
            // ordering on their own profile.
            val ordered = cards.sortedBy { it.displayOrder }
            // Never open on a frozen card (owner's subscription lapsed): promote
            // the first non-frozen (default) card to the front so the "main"
            // card shown first is an unlocked one. Default cards are never frozen.
            gallery = if (ordered.firstOrNull()?.frozen == true) {
                val firstUnlocked = ordered.firstOrNull { !it.frozen }
                if (firstUnlocked != null) {
                    listOf(firstUnlocked) + ordered.filter { it.id != firstUnlocked.id }
                } else ordered
            } else ordered
        }
    }

    // Viewer interests — used to highlight chips on the About tab when the
    // viewer shares the interest with the person being inspected. Lower-cased
    // for case-insensitive matching against PersonDto values. Loaded once
    // per session; if `getMe()` fails the sets stay empty, which simply
    // means no chips are highlighted (safe fallback).
    var viewerSports by remember { mutableStateOf<Set<String>>(emptySet()) }
    var viewerActivities by remember { mutableStateOf<Set<String>>(emptySet()) }
    var viewerLifestyle by remember { mutableStateOf<Set<String>>(emptySet()) }
    LaunchedEffect(Unit) {
        userRepository?.getMe()?.onSuccess { me ->
            viewerSports = me.interestSports.map { it.lowercase() }.toSet()
            viewerActivities = me.interestActivities.map { it.lowercase() }.toSet()
            viewerLifestyle = me.interestLifestyle.map { it.lowercase() }.toSet()
        }
    }

    // Which interest chip's note popup is currently open (if any). Hoisted
    // to the dialog level so the modal sits on top of everything — same
    // pattern as `DetailsPreferenceDialog` is invoked from the owner profile.
    var noteOpenForId by remember(person.userId) { mutableStateOf<String?>(null) }

    // Posts are lazy-loaded the first time the Posts tab is opened for this
    // person (and re-fetched when the person changes). Backend endpoint:
    // `GET v1/users/{id}/posts`.
    var posts by remember(person.userId) { mutableStateOf<List<UserPostDto>>(emptyList()) }
    var postsLoading by remember(person.userId) { mutableStateOf(false) }
    var postsLoaded by remember(person.userId) { mutableStateOf(false) }
    LaunchedEffect(person.userId, activeTab) {
        if (activeTab == DetailTab.Posts && !postsLoaded && userRepository != null) {
            postsLoading = true
            userRepository.getUserPosts(
                person.userId,
                mapOf("page" to "1", "size" to "20"),
            ).onSuccess { page -> posts = page.items }
            postsLoading = false
            postsLoaded = true
        }
    }

    // Awards lazy-load the first time the Achievement tab opens (like posts).
    var awards by remember(person.userId) { mutableStateOf<List<UserAwardDto>>(emptyList()) }
    var awardsLoaded by remember(person.userId) { mutableStateOf(false) }
    LaunchedEffect(person.userId, activeTab) {
        if (activeTab == DetailTab.Achievement && !awardsLoaded && userRepository != null) {
            userRepository.getUserAwards(person.userId).onSuccess { awards = it }
            awardsLoaded = true
        }
    }

    // Optimistic like toggle — flips the local UI immediately, then hits the
    // API. On failure we revert to the original post. Matches the pattern
    // used in PlayerHubSection for the owner's own feed; `type=USER` because
    // these are user posts (vs club posts).
    val onLikeClick: (UserPostDto) -> Unit = onLike@{ post ->
        val repo = feedRepository ?: return@onLike
        val wasLiked = post.isLikedByMe
        val optimistic = post.copy(
            isLikedByMe = !wasLiked,
            likesCount = (post.likesCount + if (wasLiked) -1 else 1).coerceAtLeast(0),
        )
        posts = posts.map { if (it.id == post.id) optimistic else it }
        scope.launch {
            repo.likePost(post.id, "USER").onFailure {
                posts = posts.map { if (it.id == post.id) post else it }
            }
        }
    }

    // Deck-style navigation: wrap around so a continuous swipe cycles
    // through the gallery the same way the profile carousel does.
    val goPrev: () -> Unit = {
        if (gallery.size > 1) galleryIndex = (galleryIndex - 1 + gallery.size) % gallery.size
    }
    val goNext: () -> Unit = {
        if (gallery.size > 1) galleryIndex = (galleryIndex + 1) % gallery.size
    }

    // Keyboard navigation — ArrowLeft / ArrowRight step through the gallery,
    // Escape dismisses the popup. Re-bound whenever the open person or
    // gallery size changes so the closures capture the current state.
    DisposableEffect(person.userId, gallery.size, galleryIndex) {
        val handler: (Event) -> Unit = handler@{ event ->
            val ke = event.asDynamic()
            when (ke.key as String) {
                "ArrowLeft" -> {
                    if (gallery.size > 1) {
                        event.preventDefault()
                        goPrev()
                    }
                }
                "ArrowRight" -> {
                    if (gallery.size > 1) {
                        event.preventDefault()
                        goNext()
                    }
                }
                "Escape" -> onDismiss()
            }
        }
        document.addEventListener("keydown", handler)
        onDispose { document.removeEventListener("keydown", handler) }
    }

    if (isMobile()) {
        PersonDetailSheet(
            person = person,
            sportName = sportName,
            gallery = gallery,
            galleryIndex = galleryIndex,
            onPrev = goPrev,
            onNext = goNext,
            onSelectCard = { galleryIndex = it },
            activeTab = activeTab,
            onSelectTab = { activeTab = it },
            viewerSports = viewerSports,
            viewerActivities = viewerActivities,
            viewerLifestyle = viewerLifestyle,
            onNoteClick = { id -> noteOpenForId = id },
            posts = posts,
            postsLoading = postsLoading,
            postsLoaded = postsLoaded,
            onLikeClick = onLikeClick,
            awards = awards,
            onDismiss = onDismiss,
        )
    } else {
    Box(
        modifier = Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(1000)
            .styleModifier { property("background", "rgba(2, 4, 11, 0.8)") }
            .onClick { onDismiss() },
        contentAlignment = Alignment.Center,
    ) {
        Box(
            modifier = Modifier
                .sMaxWidth(1100)
                .fillMaxWidth()
                .sMargin(16)
                .sBorderRadius(28)
                .sPadding(22)
                .onClick { it.stopPropagation() }
                .then(HiddenScrollbarStyle.toModifier())
                .styleModifier {
                    property("background", DIALOG_BG)
                    property("max-height", "calc(100vh - ${s(32)})")
                    property("overflow-y", "auto")
                    // Belt-and-braces against the CSS rule that overflow-y:
                    // auto promotes overflow-x to auto when any descendant
                    // pokes past the right edge (positioned children,
                    // negative-margin chips, etc.). Locks the popup width.
                    property("overflow-x", "hidden")
                    property("position", "relative")
                },
        ) {
            // Close — Figma 7636:196594: 48×48 gradient disc with the same
            // ring-border as other outline pills (mask-composite trick), small
            // X icon centered.
            CloseButton(onDismiss = onDismiss)

            Row(
                modifier = Modifier.fillMaxWidth().sGap(28),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                // Left — card visual. When the person has a multi-card
                // gallery we wrap it in a carousel (Figma 7636:196537);
                // otherwise we just render the single feed card.
                CardGallery(
                    person = person,
                    sportName = sportName,
                    gallery = gallery,
                    currentIndex = galleryIndex,
                    onPrev = goPrev,
                    onNext = goNext,
                )

                // Vertical divider between card and info column. Sized to
                // match the right column / card column height so it spans
                // the whole popup interior.
                VerticalDivider(heightPx = 565)

                // Right — info column. Height pinned to the vertical
                // divider so the dialog footprint stays identical across
                // tabs (e.g. switching to the long Posts list scrolls
                // internally instead of pushing the popup taller).
                Column(
                    modifier = Modifier
                        .sWidth(538)
                        // Match the left card column's full height (card 553
                        // + pagination row ~12) so the popup outer footprint
                        // is the same as before the fixed-height refactor,
                        // and the two columns visually balance.
                        .sHeight(565)
                        .sGap(24),
                ) {
                    HeaderBlock(person = person, sportName = sportName)
                    TabsBar(active = activeTab, onSelect = { activeTab = it })
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .then(HiddenScrollbarStyle.toModifier())
                            .styleModifier {
                                property("flex", "1 1 0")
                                property("min-height", "0")
                                property("overflow-y", "auto")
                                // Defensive: keep horizontal overflow clipped
                                // so any 1px positioned-descendant overhang
                                // (e.g. NoteDot) can't trigger a horizontal
                                // scrollbar via overflow-y:auto → overflow-x
                                // becomes auto by CSS spec.
                                property("overflow-x", "hidden")
                            },
                    ) {
                        when (activeTab) {
                            DetailTab.About -> AboutContent(
                                person = person,
                                viewerSports = viewerSports,
                                viewerActivities = viewerActivities,
                                viewerLifestyle = viewerLifestyle,
                                onNoteClick = { id -> noteOpenForId = id },
                            )
                            DetailTab.Posts -> PostsContent(
                                posts = posts,
                                person = person,
                                sportName = sportName,
                                isLoading = postsLoading,
                                loaded = postsLoaded,
                                onLikeClick = onLikeClick,
                            )
                            DetailTab.Achievement -> AwardsGridContent(awards = awards)
                        }
                    }
                }
            }
        }
    }
    }

    // Preference-details popup — same component used on the owner's profile
    // (Figma 7844:341021). Lives outside the popup Box so its own backdrop
    // (z-index 1100) sits cleanly above this dialog's z-index 1000 content.
    val openId = noteOpenForId
    if (openId != null) {
        val note = person.interestNotes[openId]
        if (!note.isNullOrBlank()) {
            DetailsPreferenceDialog(
                option = InterestCatalog.lookup(openId),
                note = note,
                onDismiss = { noteOpenForId = null },
            )
        }
    }
}

/**
 * Profile-card carousel for the detail popup left column. Mirrors the
 * profile sidebar (`OverviewSidebar.CardCarousel`) exactly — same
 * PlayerCard.Large at native 254×385, same linear peek geometry
 * (`depth*14` X offset, `1.0 - depth*0.08` scale, opacity 0.55→0.35),
 * just rendered as front-only (no flip) and wrapped in the popup's
 * stacking-context Box so absolute peeks anchor here.
 */
@Composable
private fun CardGallery(
    person: PersonDto,
    sportName: String,
    gallery: List<ProfileCardDto>,
    currentIndex: Int,
    onPrev: () -> Unit,
    onNext: () -> Unit,
    // Desktop renders the deck at 1.5× with asymmetric side margins; the mobile
    // sheet passes a smaller scale, centers it, and shows tappable chevrons.
    deckScale: Double = 1.5,
    centered: Boolean = false,
    showArrows: Boolean = false,
    // Mobile only: when set, renders pagination dots under the deck so a
    // multi-card gallery reads as browsable (the centered 1× deck hides the
    // peek slivers the desktop relies on). Tapping a dot jumps to that card.
    onSelectIndex: ((Int) -> Unit)? = null,
    // Suppress the name printed on the card art (mobile sheet).
    hideName: Boolean = false,
) {
    val safeIndex = currentIndex.coerceIn(0, (gallery.size - 1).coerceAtLeast(0))
    val current = gallery.getOrNull(safeIndex)
    val hasGallery = gallery.size > 1

    // Person's first name uppercased — matches the profile's PlayerCard
    // labelling (firstName from fullName, fall back to username).
    val playerName = (person.fullName.trim().split(" ").firstOrNull()
        ?.takeIf { it.isNotBlank() } ?: person.username).uppercase()
    val shownName = if (hideName) "" else playerName

    // Back-stack: nearest peek = next card, farthest = previous card. Same
    // wrap-around formula as the profile carousel so a freshly-swiped card
    // visibly lands at the bottom of the deck on the next render.
    val peeks: List<ProfileCardDto> = when {
        gallery.size <= 1 -> emptyList()
        gallery.size == 2 -> listOf(gallery[(safeIndex + 1) % gallery.size])
        else -> listOf(
            gallery[(safeIndex + 1) % gallery.size],
            gallery[(safeIndex - 1 + gallery.size) % gallery.size],
        )
    }

    // Drag progress fed up from SwipeableDeckCard so the peek stack can
    // promote toward the active position while the user is mid-swipe.
    var peekProgress by remember(person.userId) { mutableStateOf(0.0) }
    var peekDragging by remember(person.userId) { mutableStateOf(false) }

    // The popup has more space than the profile sidebar so we render the
    // whole deck at [deckScale]× via the nested-Box transform trick: the
    // outer Box reserves the SCALED footprint as real layout space, and
    // the inner Box stays at the native PlayerCard footprint with
    // `transform: scale()` so every child (active card + peeks) blows up
    // together — visual ratios stay identical to the profile sidebar.
    val nativeW = PlayerCardSize.Large.w
    val nativeH = PlayerCardSize.Large.h
    val scaledW = (nativeW * deckScale).toInt()
    val scaledH = (nativeH * deckScale).toInt()
    // Outer column hosts the "N / M" pagination counter (Figma 7636:196542)
    // flush with the deck's top-right corner. Side margins give the deck
    // breathing room from the popup's left padding and the vertical
    // divider.
    Column(
        modifier = Modifier
            .sWidth(scaledW)
            .styleModifier {
                if (!centered) {
                    property("margin-left", s(20))
                    property("margin-right", s(32))
                }
            },
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        if (hasGallery) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .sHeight(18)
                    .sPaddingBottom(8),
                horizontalArrangement = Arrangement.End,
            ) {
                SpanText(
                    "${safeIndex + 1} / ${gallery.size}",
                    modifier = Modifier
                        .sFontSize(18)
                        .fontWeight(AppTypography.Regular)
                        .sLineHeight(18)
                        .sLetterSpacing(0.5)
                        .color(com.arena_web.ui.theme.ThemeVars.TextMuted),
                )
            }
        }
        Box(
            modifier = Modifier
                .sWidth(scaledW)
                .sHeight(scaledH)
                .styleModifier { property("position", "relative") },
        ) {
      // Tappable chevrons in the side gutters (mobile sheet only). Negative
      // offsets place them just outside the card so they read like the Figma
      // ‹ › arrows without overlapping the art.
      if (showArrows && hasGallery) {
          GalleryArrow(iconPath = AppIcons.ArrowLeftLarge, left = true, onClick = onPrev)
          GalleryArrow(iconPath = AppIcons.ArrowRightLarge, left = false, onClick = onNext)
      }
      Box(
        modifier = Modifier.styleModifier {
            property("position", "absolute")
            property("top", "0")
            property("left", "0")
            property("width", s(nativeW))
            property("height", s(nativeH))
            property("transform", "scale($deckScale)")
            property("transform-origin", "top left")
        },
    ) {
    // `position: relative` + `isolation: isolate` anchor the peek cards'
    // absolute positioning here and contain their negative z-index within
    // this Box's stacking context — without these, peeks bubble up to the
    // popup container and vanish behind ancestor backgrounds.
    // `clip-path` extends overflow horizontally so the swipe fly-out
    // animation (peaks at ~320 px) stays visible without bleeding into the
    // adjacent info column.
    Box(
        modifier = Modifier
            .sWidth(nativeW)
            .sHeight(nativeH)
            .styleModifier {
                property("position", "relative")
                property("isolation", "isolate")
                property("clip-path", "inset(0 -${s(360)} 0 -${s(360)})")
            },
        contentAlignment = Alignment.Center,
    ) {
        // Peek cards — farthest first so they layer correctly behind the
        // active card. Same inline formula as the profile carousel: each
        // peek interpolates from its resting offset/scale/opacity toward
        // the slot above (depth-1) as the user drags, cubed for ease-in.
        peeks.reversed().forEachIndexed { reverseIdx, peek ->
            val depth = peeks.size - reverseIdx
            val baseTx = depth * 14.0
            val baseScale = 1.0 - (depth * 0.08)
            val baseOpacity = (0.55 - (depth - 1) * 0.2).coerceAtLeast(0.15)
            val promotedTx = (depth - 1) * 14.0
            val promotedScale = 1.0 - ((depth - 1) * 0.08)
            val promotedOpacity = if (depth == 1) 1.0
                else (0.55 - (depth - 2) * 0.2).coerceAtLeast(0.15)
            val t = peekProgress * peekProgress * peekProgress
            val tx = baseTx + (promotedTx - baseTx) * t
            val sc = baseScale + (promotedScale - baseScale) * t
            val op = baseOpacity + (promotedOpacity - baseOpacity) * t
            Box(
                modifier = Modifier.styleModifier {
                    property("position", "absolute")
                    property("transform", "translateX(${tx}px) scale($sc)")
                    property("transform-origin", "center")
                    property("opacity", "$op")
                    property("pointer-events", "none")
                    property("z-index", "${-depth}")
                    property(
                        "transition",
                        if (peekDragging) "none"
                        else "transform 0.36s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.3s ease"
                    )
                    property("will-change", "transform, opacity")
                }
            ) {
                PlayerCard(
                    playerName = shownName,
                    rating = 0,
                    position = "",
                    sport = sportName,
                    size = PlayerCardSize.Large,
                    avatarUrl = peek.avatarUrl ?: person.avatarUrl,
                    hideRating = true,
                    stats = emptyList(),
                    cardAssetsBasePath = peek.assetsBasePath,
                    cardTextColor = peek.textColor,
                )
            }
        }
        // Active card — swipe-left advances, swipe-right backs up. The deck
        // wrapper holds the peek stack at the promoted state during the
        // fly-to-back animation so the new active card is already docked at
        // centre when the index swaps.
        SwipeableDeckCard(
            enabled = hasGallery,
            onNext = onNext,
            onPrev = onPrev,
            onDragProgress = { p, d ->
                peekProgress = p
                peekDragging = d
            },
        ) {
            Box(
                modifier = Modifier.styleModifier { property("position", "relative") },
                contentAlignment = Alignment.Center,
            ) {
                PlayerCard(
                    playerName = shownName,
                    rating = 0,
                    position = "",
                    sport = sportName,
                    size = PlayerCardSize.Large,
                    // Frozen cards keep their REAL generated avatar — the PRO+
                    // chip overlay gates them without hiding the art.
                    avatarUrl = current?.avatarUrl ?: person.avatarUrl,
                    hideRating = true,
                    stats = emptyList(),
                    cardAssetsBasePath = current?.assetsBasePath,
                    cardTextColor = current?.textColor,
                )
                if (current?.frozen == true) {
                    com.arena_web.ui.components.cards.FrozenCardOverlay()
                }
            }
        }
    }
      }
        }

        if (onSelectIndex != null && hasGallery) {
            Row(
                modifier = Modifier.sGap(4).sPaddingTop(12),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                for (i in gallery.indices) {
                    val active = i == safeIndex
                    Box(
                        modifier = Modifier
                            .then(if (active) Modifier.sSize(6) else Modifier.sSize(4))
                            .sBorderRadius(100)
                            .cursor(Cursor.Pointer)
                            .onClick { onSelectIndex(i) }
                            .styleModifier {
                                property("background", if (active) "#0061FF" else "WRAP--l-hover2WRAP--l-hover2var(--l-hover2, #091A40)")
                            },
                    )
                }
            }
        }
    }
}

// =============================================================================
// Mobile — bottom-sheet variant (Figma 8171:175230 About / 8555:189812 Posts).
// The desktop two-column layout reflows into a single scrolling column: a
// centered avatar + name + zodiac header, the card gallery, the tabs, then the
// tab content. All inner pieces are reused as-is — the sheet aliases `--scale`
// to `--mobile-scale`, so the `s()`-sized composables render at the 390-px base.
// =============================================================================
@Composable
private fun PersonDetailSheet(
    person: PersonDto,
    sportName: String,
    gallery: List<ProfileCardDto>,
    galleryIndex: Int,
    onPrev: () -> Unit,
    onNext: () -> Unit,
    onSelectCard: (Int) -> Unit,
    activeTab: DetailTab,
    onSelectTab: (DetailTab) -> Unit,
    viewerSports: Set<String>,
    viewerActivities: Set<String>,
    viewerLifestyle: Set<String>,
    onNoteClick: (String) -> Unit,
    posts: List<UserPostDto>,
    postsLoading: Boolean,
    postsLoaded: Boolean,
    onLikeClick: (UserPostDto) -> Unit,
    awards: List<UserAwardDto>,
    onDismiss: () -> Unit,
) {
    BottomSheet(onDismiss = onDismiss, fullHeight = true) {
        // The whole sheet scrolls; min-height:0 lets it shrink inside the panel
        // flex column and overflow-x:hidden contains the gallery's clip-path.
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .then(HiddenScrollbarStyle.toModifier())
                .styleModifier {
                    property("flex", "1 1 auto")
                    property("min-height", "0")
                    property("overflow-y", "auto")
                    property("overflow-x", "hidden")
                },
        ) {
            Column(
                modifier = Modifier.fillMaxWidth().sGap(24),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                // Card (photo) leads the sheet; the identity data (avatar, name,
                // location, follow counts) sits below the gallery.
                CardGallery(
                    person = person,
                    sportName = sportName,
                    gallery = gallery,
                    currentIndex = galleryIndex,
                    onPrev = onPrev,
                    onNext = onNext,
                    deckScale = 1.0,
                    centered = true,
                    onSelectIndex = onSelectCard,
                    hideName = true,
                )

                MobileDetailHeader(person = person, sportName = sportName)

                Column(modifier = Modifier.fillMaxWidth().sGap(24)) {
                    TabsBar(active = activeTab, onSelect = onSelectTab)
                    when (activeTab) {
                        DetailTab.About -> AboutContent(
                            person = person,
                            viewerSports = viewerSports,
                            viewerActivities = viewerActivities,
                            viewerLifestyle = viewerLifestyle,
                            onNoteClick = onNoteClick,
                        )
                        DetailTab.Posts -> PostsContent(
                            posts = posts,
                            person = person,
                            sportName = sportName,
                            isLoading = postsLoading,
                            loaded = postsLoaded,
                            onLikeClick = onLikeClick,
                        )
                        DetailTab.Achievement -> AwardsGridContent(awards = awards)
                    }
                }

                // Trailing spacer so the last row clears the sheet's bottom edge
                // (Chrome/Safari drop padding-bottom on a flex scroll container).
                Box(modifier = Modifier.fillMaxWidth().sHeight(12))
            }
        }
    }
}

@Composable
private fun MobileDetailHeader(person: PersonDto, sportName: String) {
    Column(
        modifier = Modifier.fillMaxWidth().sGap(12),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        // Avatar circle removed — the card gallery above already leads with the
        // person's photo, so a second round avatar here is redundant.

        // Name + zodiac chip.
        Row(
            modifier = Modifier.sGap(12),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SpanText(
                "${person.fullName.shortName()}, ${person.age}",
                modifier = Modifier
                    .sFontSize(20)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(26)
                    .sLetterSpacing(0.2)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2),
            )
            val zodiac = computeZodiacSign(person.dateOfBirth)
            if (zodiac != null) {
                VerticalDivider(heightPx = 24)
                MobileZodiacChip(label = zodiac.label, iconPath = zodiac.iconPath)
            }
        }

        // Location + languages (📍 City, Country · Lang, Lang).
        Row(
            modifier = Modifier.sGap(6),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Icon(src = AppIcons.Location, modifier = Modifier.sSize(16))
            SpanText(
                buildString {
                    append(person.city)
                    person.country?.takeIf { it.isNotBlank() }?.let { append(", "); append(it) }
                    if (person.languages.isNotEmpty()) {
                        append(" • ")
                        append(person.languages.joinToString(", "))
                    }
                },
                modifier = Modifier
                    .sFontSize(16)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(18)
                    .color(com.arena_web.ui.theme.ThemeVars.TextMuted),
            )
        }

        // Following / Followers pills.
        Row(
            modifier = Modifier.sGap(12).sPaddingTop(4),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            CountPill(label = LocalAppStrings.current.personDetailFollowing, count = person.followingCount)
            CountPill(label = LocalAppStrings.current.personDetailFollowers, count = person.followersCount)
        }
    }
}

// Zodiac as a labelled pill (mobile shows the sign name beside the glyph; the
// desktop header uses an icon-only disc).
@Composable
private fun MobileZodiacChip(label: String, iconPath: String) {
    Box(
        modifier = Modifier
            .sHeight(32)
            .styleModifier {
                property("position", "relative")
                property("border-radius", s(100))
                property("background", INTERACTIVE_TRANSLUCENT_BG)
            },
    ) {
        GradientBorderRing(radiusPx = 100)
        Row(
            modifier = Modifier
                .fillMaxHeight()
                .sPadding(0, 12)
                .sGap(6),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Icon(src = iconPath, modifier = Modifier.sSize(18))
            SpanText(
                label,
                modifier = Modifier
                    .sFontSize(14)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(18)
                    .sLetterSpacing(0.028)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2),
            )
        }
    }
}

// Tappable gallery chevron in the side gutter (mobile sheet). Negative offset
// parks it just outside the card edge.
@Composable
private fun GalleryArrow(iconPath: String, left: Boolean, onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .sSize(40)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("position", "absolute")
                property("top", "50%")
                if (left) property("left", s(-36)) else property("right", s(-36))
                property("transform", "translateY(-50%)")
                property("z-index", "5")
            },
        contentAlignment = Alignment.Center,
    ) {
        Icon(src = iconPath, modifier = Modifier.sSize(24))
    }
}

@Composable
private fun CloseButton(onDismiss: () -> Unit) {
    Box(
        modifier = Modifier
            .styleModifier {
                property("position", "absolute")
                // Raised flush with the dialog top edge per the latest
                // design pass — the popup no longer has a border so the
                // close disc sits right at the corner.
                property("top", "0")
                property("right", s(16))
            },
    ) {
        Box(
            modifier = Modifier
                .sSize(48)
                .sBorderRadius(100)
                .cursor(Cursor.Pointer)
                .onClick { onDismiss() }
                .styleModifier {
                    property("position", "relative")
                    property("background", INTERACTIVE_TRANSLUCENT_BG)
                },
            contentAlignment = Alignment.Center,
        ) {
            GradientBorderRing(radiusPx = 100)
            Icon(src = AppIcons.Close, modifier = Modifier.sSize(16))
        }
    }
}

@Composable
private fun VerticalDivider(heightPx: Number) {
    // Same fade as the horizontal `SectionGradientDivider` in
    // ProfileEditShell — transparent at each end, peaks at 30% grey in the
    // middle — but oriented top-to-bottom.
    Box(
        modifier = Modifier
            .sHeight(heightPx)
            .styleModifier {
                property("width", "1px")
                property("flex-shrink", "0")
                property(
                    "background",
                    "linear-gradient(180deg, rgba(148, 149, 152, 0) 0%, rgba(148, 149, 152, 0.3) 50%, rgba(148, 149, 152, 0) 100%)"
                )
            },
    )
}

@Composable
private fun HeaderBlock(person: PersonDto, sportName: String) {
    Column(modifier = Modifier.fillMaxWidth().sGap(24)) {
        Row(
            modifier = Modifier.fillMaxWidth().sGap(12),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            // Mini player card — the person's main card with their AI-
            // generated avatar slot. Replaces the prior circular avatar so
            // the popup leads with the card identity that owns this
            // profile (matches how PlayerHubSection presents the owner on
            // their own profile feed).
            PersonMiniCard(person = person, sportName = sportName)

            Row(
                modifier = Modifier.flexGrow(1).sGap(18),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                // Intrinsically sized so the trailing divider + zodiac sit
                // right next to the city/languages line instead of being
                // pushed all the way to the right edge of the header row.
                Column(modifier = Modifier.sGap(6)) {
                    SpanText(
                        "${person.fullName.shortName()}, ${person.age}",
                        modifier = Modifier
                            .sFontSize(20)
                            .fontWeight(AppTypography.Medium)
                            .sLineHeight(26)
                            .sLetterSpacing(0.2)
                            .color(com.arena_web.ui.theme.ThemeVars.Text2),
                    )
                    Row(
                        modifier = Modifier.sGap(8),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Row(
                            modifier = Modifier.sGap(6),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            Icon(src = AppIcons.Location, modifier = Modifier.sSize(16))
                            SpanText(
                                buildString {
                                    append(person.city)
                                    person.country?.takeIf { it.isNotBlank() }?.let {
                                        append(", ")
                                        append(it)
                                    }
                                },
                                modifier = Modifier
                                    .sFontSize(16)
                                    .fontWeight(AppTypography.Medium)
                                    .sLineHeight(18)
                                    .color(com.arena_web.ui.theme.ThemeVars.TextMuted),
                            )
                        }
                        if (person.languages.isNotEmpty()) {
                            SpanText(
                                "• ${person.languages.joinToString(", ")}",
                                modifier = Modifier
                                    .sFontSize(16)
                                    .fontWeight(AppTypography.Medium)
                                    .sLineHeight(18)
                                    .color(com.arena_web.ui.theme.ThemeVars.TextMuted),
                            )
                        }
                    }
                }

                // Mini vertical divider + zodiac chip (Figma 7636:196578 +
                // 7640:284243). Zodiac is computed from DOB inline.
                val zodiac = computeZodiacSign(person.dateOfBirth)
                if (zodiac != null) {
                    VerticalDivider(heightPx = 48)
                    ZodiacChip(iconPath = zodiac.iconPath)
                }
            }
        }

        // Following / Followers pills — read-only counters per the
        // discovery-feed spec (no Follow action here).
        Row(modifier = Modifier.sGap(18)) {
            CountPill(label = LocalAppStrings.current.personDetailFollowing, count = person.followingCount)
            CountPill(label = LocalAppStrings.current.personDetailFollowers, count = person.followersCount)
        }
    }
}

/**
 * Tiny player-card avatar used in the popup header and on each post card.
 * Falls back to a circular avatar when the person has no card art yet, so
 * users with no card don't render an empty slab.
 */
@Composable
private fun PersonMiniCard(person: PersonDto, sportName: String) {
    val cardImageUrl = person.cardImageUrl
    if (!cardImageUrl.isNullOrBlank()) {
        PlayerCard(
            playerName = "",
            rating = 0,
            position = "",
            sport = sportName,
            size = PlayerCardSize.Highlight,
            cardAssetsBasePath = person.cardAssetsBasePath,
            cardTextColor = person.cardTextColor,
            cardFrameUrl = cardImageUrl,
            avatarUrl = person.avatarUrl,
            hideRating = true,
            hideShadow = true,
        )
    } else {
        Box(
            modifier = Modifier
                .sSize(44)
                .sBorderRadius(100)
                .styleModifier {
                    property("background-color", "#b4ccf6")
                    property("background-image", "url('${person.avatarUrl}')")
                    property("background-size", "cover")
                    property("background-position", "center")
                    property("flex-shrink", "0")
                },
        )
    }
}

@Composable
private fun ZodiacChip(iconPath: String) {
    Box(
        modifier = Modifier
            .sSize(44)
            .sBorderRadius(100)
            .styleModifier {
                property("position", "relative")
                property("background", INTERACTIVE_TRANSLUCENT_BG)
                property("overflow", "hidden")
            },
        contentAlignment = Alignment.Center,
    ) {
        GradientBorderRing(radiusPx = 100)
        Icon(src = iconPath, modifier = Modifier.sSize(22))
    }
}

@Composable
private fun CountPill(label: String, count: Long) {
    val sportColors = LocalSportColors.current
    // Outer Box owns only the pill silhouette (size, radius, background) and
    // is the position-relative anchor for the gradient ring. Padding lives on
    // the inner Row — putting it on the outer Box would shrink the absolutely
    // positioned `GradientBorderRing` inside the padding box and cut the
    // gradient border short on every edge.
    Box(
        modifier = Modifier
            .sHeight(44)
            .styleModifier {
                property("position", "relative")
                property("border-radius", s(22))
                property("background", INTERACTIVE_TRANSLUCENT_BG)
            },
    ) {
        GradientBorderRing(radiusPx = 22)
        Row(
            modifier = Modifier
                .fillMaxHeight()
                .sGap(8)
                .styleModifier {
                    property("padding", "${s(13)} ${s(16)} ${s(13)} ${s(22)}")
                },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SpanText(
                label,
                modifier = Modifier
                    .sFontSize(16)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(18)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2),
            )
            // Number bubble — mirrors the profile sidebar's CountPill
            // (OverviewSidebar.kt) so the digit stays vertically centred
            // inside the 20px disc: no explicit line-height, tabular nums
            // for stable width across counts, overflow:hidden clips any
            // sub-pixel ascender leak from the round mask.
            Box(
                modifier = Modifier
                    .sSize(20)
                    .sBorderRadius(100)
                    .backgroundColor(sportColors.primary)
                    .overflow(Overflow.Hidden),
                contentAlignment = Alignment.Center,
            ) {
                SpanText(
                    "$count",
                    modifier = Modifier
                        .sFontSize(14)
                        .fontWeight(AppTypography.Regular)
                        .color(sportColors.onPrimary)
                        .styleModifier {
                            property("font-feature-settings", "\"tnum\"")
                            property("text-align", "center")
                        },
                )
            }
        }
    }
}

@Composable
private fun TabsBar(active: DetailTab, onSelect: (DetailTab) -> Unit) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    val activeRgb = sportColors.primary.toRgb()
        .let { "rgb(${it.red}, ${it.green}, ${it.blue})" }

Row(modifier = Modifier.fillMaxWidth()) {
        DetailTab.entries.forEach { tab ->
            val isActive = tab == active
            Column(
                modifier = Modifier
                    .flexGrow(1f)
                    .cursor(Cursor.Pointer)
                    .onClick { onSelect(tab) },
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                SpanText(
                    when (tab) {
                        DetailTab.About -> t.personDetailTabAbout
                        DetailTab.Posts -> t.personDetailTabPosts
                        DetailTab.Achievement -> t.personDetailTabAchievement
                    },
                    modifier = Modifier
                        .sFontSize(18)
                        .fontWeight(AppTypography.Medium)
                        .sLineHeight(22)
                        .sLetterSpacing(0.2)
                        .color(if (isActive) org.jetbrains.compose.web.css.Color(AppColors.Text.Primary.toString()) else com.arena_web.ui.theme.ThemeVars.Text2),
                )
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .sHeight(2)
                        .styleModifier {
                            property("margin-top", s(10))
                            property(
                                "background",
                                if (isActive) activeRgb else "rgba(148, 149, 152, 0.3)",
                            )
                        },
                )
            }
        }
    }
}

@Composable
private fun AboutContent(
    person: PersonDto,
    viewerSports: Set<String>,
    viewerActivities: Set<String>,
    viewerLifestyle: Set<String>,
    onNoteClick: (String) -> Unit,
) {
    val t = LocalAppStrings.current
    Column(modifier = Modifier.fillMaxWidth().sGap(28)) {
        if (!person.bio.isNullOrBlank()) {
            SpanText(
                person.bio,
                modifier = Modifier
                    .fillMaxWidth()
                    .sFontSize(16)
                    .fontWeight(AppTypography.Regular)
                    .sLineHeight(24)
                    .color(com.arena_web.ui.theme.ThemeVars.TextMuted),
            )
        }
        InterestSection(
            label = t.personDetailSportLabel,
            values = person.interestSports,
            notes = person.interestNotes,
            matches = viewerSports,
            onNoteClick = onNoteClick,
        )
        InterestSection(
            label = t.personDetailActivityLabel,
            values = person.interestActivities,
            notes = person.interestNotes,
            matches = viewerActivities,
            onNoteClick = onNoteClick,
        )
        InterestSection(
            label = t.personDetailLifestyleLabel,
            values = person.interestLifestyle,
            notes = person.interestNotes,
            matches = viewerLifestyle,
            onNoteClick = onNoteClick,
        )
    }
}

@Composable
private fun InterestSection(
    label: String,
    values: List<String>,
    notes: Map<String, String>,
    matches: Set<String>,
    onNoteClick: (String) -> Unit,
) {
    if (values.isEmpty()) return
    Column(modifier = Modifier.fillMaxWidth().sGap(12)) {
        SpanText(
            label,
            modifier = Modifier
                .sFontSize(14)
                .fontWeight(AppTypography.Light)
                .sLineHeight(18)
                .sLetterSpacing(0.028)
                .color(com.arena_web.ui.theme.ThemeVars.Text2),
        )
        Row(
            modifier = Modifier.fillMaxWidth().sGap(12).styleModifier {
                property("flex-wrap", "wrap")
                property("row-gap", s(8))
            },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            values.forEach { id ->
                val option = InterestCatalog.lookup(id)
                val displayLabel = option?.label ?: id.replaceFirstChar { it.uppercase() }
                // Highlight only when the viewer shares this exact interest —
                // case-insensitive match against the lower-cased viewer set
                // we built once on dialog open.
                val isActive = id.lowercase() in matches
                val hasNote = !notes[id].isNullOrBlank()
                InterestChip(
                    label = displayLabel,
                    iconSrc = option?.iconSrc,
                    isActive = isActive,
                    hasNote = hasNote,
                    onClick = if (hasNote) ({ onNoteClick(id) }) else null,
                )
            }
        }
    }
}

@Composable
private fun InterestChip(
    label: String,
    iconSrc: String?,
    isActive: Boolean,
    hasNote: Boolean,
    onClick: (() -> Unit)?,
) {
    if (isActive) {
        // Figma 8175:194689 — "current sport" highlight: filled gradient from
        // the sport-primary colour at the bottom, sport-coloured 0.3px border,
        // and a small dot puck nudged just off the trailing edge.
        ActiveInterestChip(label = label, iconSrc = iconSrc, hasNote = hasNote, onClick = onClick)
    } else {
        InactiveInterestChip(label = label, iconSrc = iconSrc, hasNote = hasNote, onClick = onClick)
    }
}

@Composable
private fun InactiveInterestChip(
    label: String,
    iconSrc: String?,
    hasNote: Boolean,
    onClick: (() -> Unit)?,
) {
    // Mirrors the `ChipPill` pattern from `OverviewSidebar` — outer Box owns
    // size + position-relative + background; padding/centering live in an
    // inner Row with `fillMaxHeight`. This is the structure that lets the
    // absolute `GradientBorderRing` actually wrap the full chip silhouette.
    Box(
        modifier = Modifier
            .sHeight(32)
            .then(if (onClick != null) Modifier.cursor(Cursor.Pointer).onClick { onClick() } else Modifier)
            .styleModifier {
                property("position", "relative")
                property("border-radius", s(22))
                property("background", INTERACTIVE_TRANSLUCENT_BG)
            },
    ) {
        GradientBorderRing(radiusPx = 22)
        Row(
            modifier = Modifier
                .fillMaxHeight()
                .sPadding(0, 14)
                .sGap(8),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            if (iconSrc != null) {
                Icon(src = iconSrc, modifier = Modifier.sSize(16))
            }
            SpanText(
                label,
                modifier = Modifier
                    .sFontSize(16)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(18)
                    .sLetterSpacing(0.032)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2),
            )
        }
        if (hasNote) NoteDot()
    }
}

@Composable
private fun ActiveInterestChip(
    label: String,
    iconSrc: String?,
    hasNote: Boolean,
    onClick: (() -> Unit)?,
) {
    val sportColors = LocalSportColors.current
    // Figma 8175:194690 — chip border + gradient use the design-system
    // `stroke-cards_hover` token (#0c42ff), not the sport-primary. Only the
    // active-state dot puck switches to the sport-primary tint.
    val fill = "linear-gradient(to top, #0c42ff 3.12%, rgba(102, 102, 102, 0) 60.116%)"
    val primaryRgb = sportColors.primary.toRgb().let { "rgb(${it.red}, ${it.green}, ${it.blue})" }

    Box(
        modifier = Modifier
            .sHeight(32)
            .then(if (onClick != null) Modifier.cursor(Cursor.Pointer).onClick { onClick() } else Modifier)
            .styleModifier {
                property("position", "relative")
                property("border-radius", s(22))
                property("background", fill)
                property("border", "0.3px solid #0c42ff")
            },
    ) {
        Row(
            modifier = Modifier
                .fillMaxHeight()
                .sPadding(0, 14)
                .sGap(8),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            if (iconSrc != null) {
                Icon(src = iconSrc, modifier = Modifier.sSize(16))
            }
            SpanText(
                label,
                modifier = Modifier
                    .sFontSize(16)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(18)
                    .sLetterSpacing(0.032)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2),
            )
        }
        // Active-state dot — 8×8 sport-primary puck anchored to the chip's
        // top-right corner, intentionally nudged outside by ~3px so it reads
        // as overlapping the chip border (Figma direction). The note-state
        // dot already lives at top-right too, so when both are true they
        // visually collapse into a single indicator.
        Box(
            modifier = Modifier
                .sSize(8)
                .styleModifier {
                    property("position", "absolute")
                    property("top", s(-3))
                    property("right", s(-3))
                    property("background-color", primaryRgb)
                    property("border", "0.5px solid var(--l-s3, #081735)")
                    property("border-radius", "100px")
                    property("z-index", "1")
                    property("pointer-events", "none")
                },
        )
        if (hasNote) NoteDot()
    }
}

/**
 * Blue-dot marker — 8×8 puck in the top-right corner of an interest chip,
 * shown when the person has a saved note for that interest. Mirrors the
 * profile pattern in `OverviewSidebar.ChipPill` (Figma 7861:353977). Sits
 * above all chip content via z-index so it stays visible regardless of
 * which chip variant renders it.
 */
@Composable
private fun NoteDot() {
    val sport = LocalSportColors.current
    // Sits flush in the chip's top-right corner (no negative offset). The
    // owner-profile sidebar uses top:-1/right:-1 for a slight overhang, but
    // here the chip lives inside the popup's scroll-y container — any
    // absolutely-positioned descendant that pokes outside its parent box
    // extends the container's scrollable overflow region. CSS treats
    // overflow-y:auto as forcing overflow-x to auto too, so a 1px overhang
    // produced both axes' scrollbars and visually shrank the popup. Keeping
    // the dot inside the chip footprint avoids that entirely.
    Box(
        modifier = Modifier
            .sSize(8)
            .styleModifier {
                property("position", "absolute")
                property("top", "0")
                property("right", "0")
                property("background-color", sport.primary.toRgb().let {
                    "rgb(${it.red}, ${it.green}, ${it.blue})"
                })
                property("border", "0.5px solid var(--l-s3, #081735)")
                property("border-radius", "100px")
                property("z-index", "1")
                property("pointer-events", "none")
            },
    )
}

@Composable
private fun PostsContent(
    posts: List<UserPostDto>,
    person: PersonDto,
    sportName: String,
    isLoading: Boolean,
    loaded: Boolean,
    onLikeClick: (UserPostDto) -> Unit,
) {
    when {
        isLoading || !loaded -> Box(
            modifier = Modifier.fillMaxWidth().sHeight(200),
            contentAlignment = Alignment.Center,
        ) {
            SpanText(
                LocalAppStrings.current.commonLoading,
                modifier = Modifier
                    .sFontSize(14)
                    .fontWeight(AppTypography.Regular)
                    .color(com.arena_web.ui.theme.ThemeVars.TextMuted),
            )
        }
        posts.isEmpty() -> PostsEmptyState()
        else -> Column(modifier = Modifier.fillMaxWidth().sGap(40)) {
            posts.forEach { post ->
                PostCard(
                    post = post,
                    person = person,
                    sportName = sportName,
                    onLikeClick = { onLikeClick(post) },
                )
            }
        }
    }
}

/**
 * Mirrors `PostListCard` from `PlayerHubSection` — the post layout used on
 * the owner's own profile feed — so the popup matches the rest of the
 * product instead of inventing a separate "compact" treatment. Image
 * collage is delegated to `PostImageCollage`, which handles all six author-
 * chosen layouts (single portrait/landscape, 2-up variants, 3-up combos).
 */
@Composable
private fun PostCard(
    post: UserPostDto,
    person: PersonDto,
    sportName: String,
    onLikeClick: () -> Unit,
) {
    Column(modifier = Modifier.fillMaxWidth().sGap(14)) {
        Row(
            modifier = Modifier.fillMaxWidth().sGap(12),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            PersonMiniCard(person = person, sportName = sportName)
            Column(modifier = Modifier.flexGrow(1).sGap(6)) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier.sGap(8),
                ) {
                    SpanText(
                        person.fullName.shortName(),
                        modifier = Modifier
                            .sFontSize(16)
                            .fontWeight(AppTypography.Medium)
                            .sLineHeight(18)
                            .color(com.arena_web.ui.theme.ThemeVars.Text1),
                    )
                    Box(
                        modifier = Modifier
                            .sSize(3)
                            .sBorderRadius(100)
                            .backgroundColor(AppColors.Text.Muted),
                    )
                    SpanText(
                        formatPostDate(post.createdAt),
                        modifier = Modifier
                            .sFontSize(16)
                            .fontWeight(AppTypography.Regular)
                            .sLineHeight(24)
                            .sLetterSpacing(0.16)
                            .color(com.arena_web.ui.theme.ThemeVars.Text3),
                    )
                }
                if (!post.city.isNullOrBlank()) {
                    SpanText(
                        post.city,
                        modifier = Modifier
                            .sFontSize(14)
                            .fontWeight(AppTypography.Light)
                            .sLineHeight(18)
                            .sLetterSpacing(0.028)
                            .color(com.arena_web.ui.theme.ThemeVars.Text3),
                    )
                }
            }
        }
        if (post.content.isNotBlank()) {
            SpanText(
                post.content,
                modifier = Modifier
                    .fillMaxWidth()
                    .sFontSize(16)
                    .fontWeight(AppTypography.Regular)
                    .sLineHeight(24)
                    .sLetterSpacing(0.16)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2),
            )
        }
        if (post.images.isNotEmpty()) {
            PostImageCollage(post.images.map { it.imageUrl }, post.layout)
        }
        Row(
            modifier = Modifier
                .sGap(8)
                .cursor(Cursor.Pointer)
                .onClick { onLikeClick() },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            com.varabyte.kobweb.silk.components.graphics.Image(
                src = AppIcons.Heart,
                modifier = Modifier.sSize(24).styleModifier {
                    if (post.isLikedByMe) {
                        // Same red tint as the owner profile feed when liked.
                        property(
                            "filter",
                            "brightness(0) saturate(100%) invert(27%) sepia(95%) " +
                                "saturate(4997%) hue-rotate(344deg) brightness(95%) contrast(99%)",
                        )
                    }
                },
            )
            SpanText(
                "${post.likesCount}",
                modifier = Modifier
                    .sFontSize(18)
                    .fontWeight(AppTypography.Regular)
                    .sLineHeight(18)
                    .sLetterSpacing(0.5)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2),
            )
        }
    }
}

private fun formatPostDate(iso: String): String = try {
    val d = kotlin.js.Date(iso)
    val months = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
    "${d.getDate()} ${months[d.getMonth()]}"
} catch (_: Throwable) { iso }

// Empty posts placeholder for the Posts tab — image-stack glyph + message,
// mirroring the profile/feed empty states. Uses s()/TintedGlyph, which work
// in both the desktop dialog and the mobile sheet (the sheet aliases --scale).
@Composable
private fun PostsEmptyState() {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier.fillMaxWidth().sPadding(v = 40, h = 0).sGap(16),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        TintedGlyph(src = AppIcons.EmptyImagesStack, sizePx = 72)
        Column(
            modifier = Modifier.fillMaxWidth().sGap(8),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            SpanText(
                t.personDetailNoPostsTitle,
                modifier = Modifier
                    .sFontSize(20)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(26)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .styleModifier { property("text-align", "center") },
            )
            SpanText(
                t.personDetailNoPostsMessage,
                modifier = Modifier
                    .fillMaxWidth()
                    .sFontSize(14)
                    .fontWeight(AppTypography.Regular)
                    .sLineHeight(22)
                    .color(com.arena_web.ui.theme.ThemeVars.TextMuted)
                    .styleModifier { property("text-align", "center") },
            )
        }
    }
}


private fun String.shortName(): String {
    val parts = trim().split(" ")
    if (parts.size <= 1) return this
    val first = parts.first()
    val lastInitial = parts.last().firstOrNull()?.uppercaseChar() ?: return first
    return "$first ${lastInitial}."
}

private data class ZodiacSign(val label: String, val iconPath: String)

/**
 * Standard astrological date ranges. Mirrors the profile sidebar's zodiac
 * helper — kept private here so the popup can render the chip without
 * depending on the profile sidebar internals.
 */
private fun computeZodiacSign(dob: String?): ZodiacSign? {
    if (dob.isNullOrBlank()) return null
    val datePart = dob.substringBefore('T').trim()
    val parts = datePart.split('-')
    if (parts.size != 3) return null
    val month = parts[1].toIntOrNull() ?: return null
    val day = parts[2].toIntOrNull() ?: return null
    return when {
        (month == 3 && day >= 21) || (month == 4 && day <= 19) -> ZodiacSign("aries", AppIcons.ZodiacAries)
        (month == 4 && day >= 20) || (month == 5 && day <= 20) -> ZodiacSign("taurus", AppIcons.ZodiacTaurus)
        (month == 5 && day >= 21) || (month == 6 && day <= 20) -> ZodiacSign("gemini", AppIcons.ZodiacGemini)
        (month == 6 && day >= 21) || (month == 7 && day <= 22) -> ZodiacSign("cancer", AppIcons.ZodiacCancer)
        (month == 7 && day >= 23) || (month == 8 && day <= 22) -> ZodiacSign("leo", AppIcons.ZodiacLeo)
        (month == 8 && day >= 23) || (month == 9 && day <= 22) -> ZodiacSign("virgo", AppIcons.ZodiacVirgo)
        (month == 9 && day >= 23) || (month == 10 && day <= 22) -> ZodiacSign("libra", AppIcons.ZodiacLibra)
        (month == 10 && day >= 23) || (month == 11 && day <= 21) -> ZodiacSign("scorpio", AppIcons.ZodiacScorpio)
        (month == 11 && day >= 22) || (month == 12 && day <= 21) -> ZodiacSign("sagittarius", AppIcons.ZodiacSagittarius)
        (month == 12 && day >= 22) || (month == 1 && day <= 19) -> ZodiacSign("capricorn", AppIcons.ZodiacCapricorn)
        (month == 1 && day >= 20) || (month == 2 && day <= 18) -> ZodiacSign("aquarius", AppIcons.ZodiacAquarius)
        (month == 2 && day >= 19) || (month == 3 && day <= 20) -> ZodiacSign("pisces", AppIcons.ZodiacPisces)
        else -> null
    }
}
